/**
 * CAP install scheduling: slots/book via install APIs; reschedule via shared
 * site-survey endpoint. Install service-appointment ids from SOW (recordType Install).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { type Sow } from "../capTypes";
import { SiteSurveyAppointment } from "../../../amplify/backend/function/OfferExpCheckout/ts/public/types";
import { useCapContext } from "../providers/CapContext";
import {
  buildScheduledInstallAppointmentFromBookedSlot,
  getReadyInstallServiceAppointment,
  getScheduledInstallServiceAppointment,
  isBookInstallSuccessful,
  mapSowInstallToSiteSurveyAppointment,
} from "../constants/capInstallScheduling";
import {
  isInstallBooking503Error,
  pollInstallBookingAfter503,
} from "../utils/installBookingRecovery";
import { useInstallAvailableServiceSlots } from "./useInstallAvailableServiceSlots";
import { type SiteSurveySlot } from "checkout/api/siteSurvey";
import { useAuthToken } from "hooks/useAuthToken";
import { bookInstallAppointment } from "services/installScheduling";
import { rescheduleSiteSurveyAppointment } from "services/prospect";

type UsePostCapInstallSchedulingOptions = {
  isSowLoading: boolean;
  mutateSow: () => Promise<unknown>;
  mutateDaysOut?: () => Promise<unknown>;
  slotsStartOffset?: number;
  consecutiveScheduling?: boolean;
};

const usePostCapInstallScheduling = (
  sow: Sow | undefined,
  prospectId: string | undefined,
  {
    isSowLoading,
    mutateSow,
    mutateDaysOut,
    slotsStartOffset,
    consecutiveScheduling,
  }: UsePostCapInstallSchedulingOptions
) => {
  const authKey = useAuthToken("HYBRID");
  const { isCustomer, hasRequestedInstallSlots, requestInstallSlots } =
    useCapContext();

  const readyInstall = useMemo(
    () => getReadyInstallServiceAppointment(sow),
    [sow]
  );
  const scheduledInstall = useMemo(
    () => getScheduledInstallServiceAppointment(sow),
    [sow]
  );

  const slotBookingParentId = readyInstall?.id;
  const [optimisticScheduledInstall, setOptimisticScheduledInstall] = useState<
    SiteSurveyAppointment | undefined
  >();
  const scheduledAppointment = useMemo(() => {
    if (scheduledInstall) {
      return mapSowInstallToSiteSurveyAppointment(scheduledInstall);
    }
    return optimisticScheduledInstall;
  }, [optimisticScheduledInstall, scheduledInstall]);

  useEffect(() => {
    if (scheduledInstall) {
      setOptimisticScheduledInstall(undefined);
    }
  }, [scheduledInstall]);

  const slotsState = useInstallAvailableServiceSlots(
    prospectId,
    slotBookingParentId,
    slotsStartOffset,
    consecutiveScheduling,
    hasRequestedInstallSlots
  );

  const [bookingLoading, setBookingLoading] = useState(false);
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [schedulingSessionKey, setSchedulingSessionKey] = useState(0);

  const bookSlot = useCallback(
    (slot: SiteSurveySlot) => {
      if (!prospectId || !authKey?.token) {
        const msg = !prospectId
          ? "bookSlot: missing prospectId"
          : "bookSlot: missing hybrid auth token";
        return Promise.reject(new Error(msg));
      }
      if (!slotBookingParentId) {
        return Promise.reject(
          new Error("bookSlot: missing install service appointment id")
        );
      }

      setBookingLoading(true);

      return bookInstallAppointment(
        authKey.token,
        prospectId,
        slotBookingParentId,
        {
          arrivalStartDateUtc: slot.arrivalStartDate,
          arrivalEndDateUtc: slot.arrivalEndDate,
          isAccelerated: true,
          isRep: !isCustomer,
        }
      )
        .then((result) => {
          if (!isBookInstallSuccessful(result)) {
            return Promise.reject(
              new Error(
                "Install booking did not complete: Salesforce slot was not booked"
              )
            );
          }
          setOptimisticScheduledInstall(
            buildScheduledInstallAppointmentFromBookedSlot(
              slot,
              slotBookingParentId
            )
          );
          mutateSow().catch((reloadErr) => {
            console.error(
              "[PostCapInstall] bookSlot SOW reload failed",
              reloadErr
            );
          });
        })
        .catch(async (err) => {
          if (isInstallBooking503Error(err)) {
            try {
              const freshSow = await pollInstallBookingAfter503(mutateSow);
              const scheduled = getScheduledInstallServiceAppointment(freshSow);
              if (scheduled) {
                setOptimisticScheduledInstall(
                  mapSowInstallToSiteSurveyAppointment(scheduled)
                );
              }
              return;
            } catch (pollErr) {
              console.error(
                "[PostCapInstall] bookSlot 503 recovery polling failed",
                pollErr
              );
            }
          }

          console.error("[PostCapInstall] bookSlot failed", err);
          try {
            await mutateSow();
          } catch (reloadErr) {
            console.error(
              "[PostCapInstall] bookSlot SOW reload failed",
              reloadErr
            );
          }
          throw err;
        })
        .finally(() => setBookingLoading(false));
    },
    [authKey?.token, isCustomer, mutateSow, prospectId, slotBookingParentId]
  );

  const rescheduleAppointment = useCallback(() => {
    if (!scheduledInstall?.id || !authKey?.token) {
      const msg = !scheduledInstall?.id
        ? "reschedule: missing appointment id"
        : "reschedule: missing hybrid auth token";
      return Promise.reject(new Error(msg));
    }
    setRescheduleLoading(true);
    return rescheduleSiteSurveyAppointment(authKey.token, {
      appointmentId: scheduledInstall.id,
      rescheduleReason: "Customer Reschedule",
      isComplexAppointment: false,
    })
      .then(() =>
        Promise.all([mutateSow(), mutateDaysOut?.() ?? Promise.resolve()])
      )
      .then(() => {
        setOptimisticScheduledInstall(undefined);
        setSchedulingSessionKey((key) => key + 1);
      })
      .catch((err) => {
        console.error("[PostCapInstall] reschedule failed", err);
        throw err;
      })
      .finally(() => setRescheduleLoading(false));
  }, [authKey?.token, mutateDaysOut, mutateSow, scheduledInstall?.id]);

  return {
    slotBookingParentId,
    scheduledAppointment,
    bookingSnapshotLoading: isSowLoading,
    slots: slotsState.data,
    slotsLoading: slotsState.isLoading,
    slotsError: slotsState.error,
    retrySlots: slotsState.retry,
    requestSlots: requestInstallSlots,
    bookingLoading,
    rescheduleLoading,
    bookSlot,
    rescheduleAppointment,
    pickerResetKey: schedulingSessionKey,
  };
};

export { usePostCapInstallScheduling };
export type { UsePostCapInstallSchedulingOptions };
